if (!customElements.get('cp-subscription-widget-v2')) {
  customElements.define('cp-subscription-widget-v2', class extends HTMLElement {
    connectedCallback() {
      if (this.controller) return;
      this.controller = new AbortController();
      queueMicrotask(() => this.initialize());
    }
    disconnectedCallback() {
      this.controller?.abort();
      this.controller = null;
    }
    initialize() {
      if (!this.isConnected || !this.controller) return;
      this.config = JSON.parse(this.querySelector('[data-cpv2-config]').textContent);
      this.form = this.querySelector('.cpv2__primary form');
      this.otpForm = this.querySelector('.cpv2__otp form');
      this.cards = [...this.querySelectorAll('[data-offer]')];
      this.formatter = new Intl.NumberFormat(document.documentElement.lang || 'en', {
        style: 'currency', currency: this.dataset.currency, minimumFractionDigits: 2, maximumFractionDigits: 2
      });
      const signal = this.controller.signal;
      this.addEventListener('click', (event) => {
        const toggle = event.target.closest('.cpv2__kit-toggle');
        if (!toggle || !this.contains(toggle)) return;
        const kit = toggle.closest('.cpv2__kit');
        const expanded = toggle.getAttribute('aria-expanded') !== 'true';
        const panel = kit.querySelector('.cpv2__kit-panel');
        kit.classList.toggle('is-open', expanded);
        toggle.setAttribute('aria-expanded', String(expanded));
        panel.inert = !expanded;
        panel.setAttribute('aria-hidden', String(!expanded));
        kit.querySelector('.cpv2__kit-preview').setAttribute('aria-hidden', String(expanded));
        kit.querySelector('.cpv2__kit-heading-shell').setAttribute('aria-hidden', String(!expanded));
      }, { signal });
      this.addEventListener('change', (event) => {
        if (event.target.matches('input[name="selling_plan"]')) this.update();
        if (event.target === this.form.elements.id) this.update();
      }, { signal });
      // Dawn can replace the variant input after the original selection event.
      this.closest('product-info')?.addEventListener('change', (event) => {
        if (event.target.matches('input[name="id"]')) this.update();
      }, { signal });
      this.form.addEventListener('submit', (event) => {
        if (!this.update()) { event.preventDefault(); event.stopImmediatePropagation(); }
      }, { capture: true, signal });
      this.update();
    }
    money(cents) { return this.formatter.format(Math.round(cents) / 100); }
    fill(template, values) { return template.replace(/\{(\w+)\}/g, (match, key) => values[key] ?? match); }
    update() {
      const variant = this.config.variants.find(item => String(item.id) === this.form.elements.id.value);
      let selectedAllocation;
      let selectedCard;
      const offers = [];
      for (const card of this.cards) {
        const radio = card.querySelector('input[type="radio"]');
        const allocation = variant?.allocations.find(item => String(item.id) === card.dataset.plan);
        const selected = radio.checked;
        radio.disabled = !allocation || !variant.available;
        card.classList.toggle('is-selected', selected);
        const details = card.querySelector('.cpv2__details-shell');
        const expanded = selected || this.dataset.expandAll === 'true';
        details.classList.toggle('is-open', expanded);
        details.inert = !expanded;
        details.setAttribute('aria-hidden', String(!expanded));
        card.querySelector('.cpv2__price-row').hidden = !allocation;
        card.querySelector('.cpv2__billing-row').hidden = !allocation;
        card.querySelector('[data-save]').hidden = !allocation;
        if (!allocation) continue;
        const quantity = Number(card.dataset.quantity);
        const months = Number(card.dataset.months);
        const total = allocation.price * quantity;
        const compare = variant.price * quantity;
        const saving = compare ? Math.round((compare - total) / compare * 100) : 0;
        offers.push({
          planId: card.dataset.plan, title: card.querySelector('.cpv2__title').textContent.trim(),
          quantity, days: Number(card.dataset.days), total, monthlyPrice: total / months, saving, available: !radio.disabled,
          servingPrice: total / (this.config.servings * quantity), dailyPrice: total / Number(card.dataset.days)
        });
        card.querySelector('[data-monthly]').textContent = this.money(total / months);
        const comparison = card.querySelector('[data-compare]');
        if (comparison) { comparison.textContent = this.money(compare / months); comparison.hidden = compare <= total; }
        card.querySelector('[data-save]').textContent = this.fill(this.config.saveText, { saving });
        card.querySelector('[data-save]').hidden = saving <= 0;
        card.querySelector('[data-billing]').textContent = this.fill(this.config.billingText, {
          total: this.money(total), currency: this.dataset.currency, days: card.dataset.days
        });
        const serving = card.querySelector('[data-serving]');
        if (serving) serving.textContent = this.fill(this.config.servingText, {
          price: this.money(total / (this.config.servings * quantity)), currency: this.dataset.currency
        });
        card.querySelectorAll('[data-benefit-template]').forEach(item => {
          item.textContent = this.fill(item.dataset.benefitTemplate, { saving });
        });
        if (selected) { selectedAllocation = allocation; selectedCard = card; }
      }
      const ready = Boolean(variant?.available && selectedAllocation);
      const button = this.form.querySelector('[type="submit"]');
      button.disabled = !ready;
      this.querySelector('.cpv2__unavailable').hidden = ready;
      if (ready) {
        this.form.elements.quantity.value = selectedCard.dataset.quantity;
        this.form.elements['properties[_herbl_bundle_size]'].value = selectedCard.dataset.quantity;
        this.querySelector('[data-cta-text]').textContent = this.config.atcLabel;
        this.querySelector('[data-cta-total]').textContent = this.money(selectedAllocation.price * Number(selectedCard.dataset.quantity));
        const compare = this.querySelector('[data-cta-compare]');
        compare.textContent = this.money(variant.price * Number(selectedCard.dataset.quantity));
        compare.hidden = variant.price <= selectedAllocation.price;
        const billing = this.querySelector('[data-cta-billing]');
        if (billing) billing.textContent = this.fill(this.config.atcBilling, { days: selectedCard.dataset.days });
      }
      if (variant) {
        const otpQuantity = Math.max(1, Math.floor(Number(selectedCard?.dataset.quantity || this.config.otpQuantity) || 1));
        const otpTotal = variant.price * otpQuantity;
        const shippingSuffix = otpTotal < this.config.shippingThreshold ? ` ${this.config.shippingSuffix}` : "";
        this.otpForm.elements.quantity.value = String(otpQuantity);
        this.otpForm.elements['properties[_herbl_bundle_size]'].value = String(otpQuantity);
        this.otpForm.elements.id.value = String(variant.id);
        this.otpForm.querySelector('[type="submit"]').disabled = !variant.available;
        this.otpForm.querySelector('[type="submit"] > span').textContent = this.fill(this.config.otpLabel, { price: this.money(otpTotal) + shippingSuffix });
      } else this.otpForm.querySelector('[type="submit"]').disabled = true;
      this.purchaseState = { offers, selectedPlan: selectedCard?.dataset.plan || '', ready, currency: this.dataset.currency };
      this.dispatchEvent(new CustomEvent('cp:subscription:change', { bubbles: true, detail: this.purchaseState }));
      return ready;
    }
  });
}
